import React, { useEffect, useState } from 'react';
import { ownerGuarantorSchemeService } from '../services/ownerGuarantorScheme.service';
import { OwnerGuarantorScheme } from '../types/candidat';
import { getErrorMessage } from '../utils/error';

const GuarantorSchemesSection: React.FC = () => {
  const [schemes, setSchemes] = useState<OwnerGuarantorScheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setSchemes(await ownerGuarantorSchemeService.list());
    } catch (e) {
      setMessage({ type: 'error', text: getErrorMessage(e, 'Erreur lors du chargement des garanties') });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (s: OwnerGuarantorScheme) => {
    try {
      const updated = await ownerGuarantorSchemeService.update(s.id, {
        is_active: !s.isActive,
      });
      setSchemes((prev) => prev.map((x) => (x.id === s.id ? updated : x)));
    } catch (e) {
      setMessage({ type: 'error', text: getErrorMessage(e, 'Erreur lors de la mise à jour') });
    }
  };

  const handleAdd = async () => {
    if (!newCode.trim() || !newLabel.trim()) return;
    setAddLoading(true);
    try {
      const created = await ownerGuarantorSchemeService.create(newCode.trim(), newLabel.trim());
      setSchemes((prev) => [...prev, created]);
      setNewCode('');
      setNewLabel('');
      setShowAdd(false);
      setMessage({ type: 'success', text: 'Garantie ajoutée.' });
    } catch (e) {
      setMessage({ type: 'error', text: getErrorMessage(e, "Erreur lors de l'ajout") });
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await ownerGuarantorSchemeService.delete(deleteId);
      setSchemes((prev) => prev.filter((s) => s.id !== deleteId));
      setDeleteId(null);
      setMessage({ type: 'success', text: 'Garantie supprimée.' });
    } catch (e) {
      setMessage({ type: 'error', text: getErrorMessage(e, 'Erreur lors de la suppression') });
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section-header">
        <h3>Garanties institutionnelles acceptées</h3>
      </div>
      <p className="settings-hint" style={{ marginBottom: '16px' }}>
        Choisissez les dispositifs de garantie (Visale, Garantme, GLI…) que vous acceptez
        pour vos colocations. Les candidats verront uniquement les organismes actifs.
      </p>

      {message && (
        <div className={`settings-message settings-message-${message.type}`}>{message.text}</div>
      )}

      {loading ? (
        <div className="settings-loading">Chargement...</div>
      ) : (
        <>
          <table className="config-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Libellé</th>
                <th>Actif</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {schemes.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ textAlign: 'center', color: '#888' }}>
                    Aucune garantie configurée.
                  </td>
                </tr>
              )}
              {schemes.map((s) => (
                <tr key={s.id}>
                  <td>
                    <code>{s.schemeCode}</code>
                  </td>
                  <td>{s.schemeLabel}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={s.isActive}
                      onChange={() => toggleActive(s)}
                    />
                  </td>
                  <td>
                    {deleteId === s.id ? (
                      <>
                        <button
                          className="delete-confirm-btn"
                          onClick={handleDelete}
                          disabled={deleteLoading}
                        >
                          {deleteLoading ? '...' : 'Confirmer'}
                        </button>
                        <button className="cancel-button" onClick={() => setDeleteId(null)}>
                          Non
                        </button>
                      </>
                    ) : (
                      <button className="cancel-button" onClick={() => setDeleteId(s.id)}>
                        Supprimer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!showAdd ? (
            <button
              className="add-config-btn"
              onClick={() => setShowAdd(true)}
              style={{ marginTop: 12 }}
            >
              + Ajouter une garantie
            </button>
          ) : (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Code (ex: VISALE)"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                style={{ flex: '0 0 160px' }}
              />
              <input
                type="text"
                placeholder="Libellé (ex: Visale)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                style={{ flex: 1, minWidth: 180 }}
              />
              <button className="save-config-btn" onClick={handleAdd} disabled={addLoading}>
                {addLoading ? '...' : 'Ajouter'}
              </button>
              <button className="cancel-button" onClick={() => setShowAdd(false)}>
                Annuler
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GuarantorSchemesSection;
